import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { omniChannelAPI, segmentsAPI, contactsAPI, usersAPI } from '../api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import { AssigneeSelect } from '../components/AssigneeSelect';
import { 
  Mail, Target, Calendar, Users, MessageSquare, CheckSquare, 
  Plus, Send, Sparkles, BarChart3, Clock, AlertCircle, TrendingUp,
  Layers, Smartphone, Globe, Bell, Megaphone, Zap, Eye, MousePointerClick,
  CheckCircle2, XCircle, Pause, Play, Edit, Trash2, RefreshCcw, Copy,
  ListTodo, ChevronRight, ArrowRight, Filter, Search, Scale, Trophy, X, Loader2
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

// Channel icons mapping
const channelIcons = {
  email: Mail,
  social_media: Globe,
  sms: Smartphone,
  push_notification: Bell,
  in_app: Megaphone,
  direct_mail: Mail,
  display_ads: Target,
  search_ads: Search
};

const channelColors = {
  email: 'bg-blue-100 text-blue-800',
  social_media: 'bg-purple-100 text-purple-800',
  sms: 'bg-green-100 text-green-800',
  push_notification: 'bg-orange-100 text-orange-800',
  in_app: 'bg-pink-100 text-pink-800',
  direct_mail: 'bg-yellow-100 text-yellow-800',
  display_ads: 'bg-indigo-100 text-indigo-800',
  search_ads: 'bg-cyan-100 text-cyan-800'
};

const priorityColors = {
  critical: 'bg-red-500 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-yellow-500 text-white',
  low: 'bg-gray-400 text-white'
};

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  planning: 'bg-blue-100 text-blue-800',
  review: 'bg-purple-100 text-purple-800',
  scheduled: 'bg-cyan-100 text-cyan-800',
  active: 'bg-green-100 text-green-800',
  paused: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-emerald-100 text-emerald-800',
  archived: 'bg-gray-200 text-gray-600'
};

export default function OmniChannelPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  
  // Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    objective: '',
    status: 'draft',
    priority: 'medium',
    start_date: '',
    end_date: '',
    total_budget: 0,
    channels: [],
    target_segments: []
  });
  
  // Email Marketing
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [contactLists, setContactLists] = useState([]);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({
    name: '',
    subject: '',
    preview_text: '',
    html_content: '',
    recipient_list_id: '',
    scheduled_at: ''
  });
  
  // Collaboration
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    assigned_to: '',
    due_date: '',
    priority: 'medium',
    channel: ''
  });
  
  // Calendar
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Segments for targeting
  const [segments, setSegments] = useState([]);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Comparison states
  const [selectedForComparison, setSelectedForComparison] = useState([]);
  const [comparing, setComparing] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  
  // Team members for task assignment
  const [teamMembers, setTeamMembers] = useState([]);

  // Load initial data
  useEffect(() => {
    loadDashboard();
    loadCampaigns();
    loadEmailCampaigns();
    loadEmailTemplates();
    loadContactLists();
    loadSegments();
    loadTeamMembers();
  }, []);
  
  const loadTeamMembers = async () => {
    try {
      const response = await usersAPI.getTeam();
      setTeamMembers(response.data || []);
    } catch (error) {
      console.error('Error loading team members:', error);
    }
  };
  
  const loadDashboard = async () => {
    try {
      const response = await omniChannelAPI.getDashboard();
      setDashboard(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    }
  };
  
  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      
      const response = await omniChannelAPI.getCampaigns(params);
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };
  
  const loadEmailCampaigns = async () => {
    try {
      const response = await omniChannelAPI.getEmailCampaigns({});
      setEmailCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Error loading email campaigns:', error);
    }
  };
  
  const loadEmailTemplates = async () => {
    try {
      const response = await omniChannelAPI.getEmailTemplates();
      setEmailTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error loading email templates:', error);
    }
  };
  
  const loadContactLists = async () => {
    try {
      const response = await omniChannelAPI.getContactLists();
      setContactLists(response.data.lists || []);
    } catch (error) {
      console.error('Error loading contact lists:', error);
    }
  };
  
  const loadSegments = async () => {
    try {
      const response = await segmentsAPI.getAll();
      setSegments(response.data || []);
    } catch (error) {
      console.error('Error loading segments:', error);
    }
  };
  
  const loadCalendarEvents = async () => {
    try {
      const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).toISOString().split('T')[0];
      const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).toISOString().split('T')[0];
      
      const response = await omniChannelAPI.getCalendar(startDate, endDate);
      setCalendarEvents(response.data.events || []);
    } catch (error) {
      console.error('Error loading calendar:', error);
    }
  };
  
  useEffect(() => {
    if (activeTab === 'calendar') {
      loadCalendarEvents();
    }
  }, [activeTab, currentMonth]);
  
  useEffect(() => {
    loadCampaigns();
  }, [statusFilter, priorityFilter]);
  
  // Campaign CRUD
  const handleCreateCampaign = async () => {
    try {
      const response = await omniChannelAPI.createCampaign(campaignForm);
      toast.success('Campaign created successfully');
      setShowCampaignDialog(false);
      setCampaignForm({
        name: '',
        description: '',
        objective: '',
        status: 'draft',
        priority: 'medium',
        start_date: '',
        end_date: '',
        total_budget: 0,
        channels: [],
        target_segments: []
      });
      loadCampaigns();
      loadDashboard();
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error('Failed to create campaign');
    }
  };
  
  const handleDeleteCampaign = async (campaignId) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await omniChannelAPI.deleteCampaign(campaignId);
      toast.success('Campaign deleted');
      loadCampaigns();
      loadDashboard();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Failed to delete campaign');
    }
  };
  
  // Email Campaign CRUD
  const handleCreateEmailCampaign = async () => {
    try {
      const response = await omniChannelAPI.createEmailCampaign(emailForm);
      toast.success('Email campaign created');
      setShowEmailDialog(false);
      setEmailForm({
        name: '',
        subject: '',
        preview_text: '',
        html_content: '',
        recipient_list_id: '',
        scheduled_at: ''
      });
      loadEmailCampaigns();
    } catch (error) {
      console.error('Error creating email campaign:', error);
      toast.error('Failed to create email campaign');
    }
  };
  
  const handleSendEmailCampaign = async (campaignId) => {
    if (!confirm('Are you sure you want to send this campaign now?')) return;
    
    try {
      const response = await omniChannelAPI.sendEmailCampaign(campaignId);
      toast.success(`Sending to ${response.data.recipient_count} recipients`);
      loadEmailCampaigns();
    } catch (error) {
      console.error('Error sending campaign:', error);
      toast.error(error.response?.data?.detail || 'Failed to send campaign');
    }
  };
  
  const handleGenerateEmailContent = async () => {
    if (!emailForm.name) {
      toast.error('Please enter a topic first');
      return;
    }
    
    try {
      toast.info('Generating email content with AI...');
      const response = await omniChannelAPI.generateEmail(emailForm.name);
      setEmailForm(prev => ({
        ...prev,
        subject: response.data.subject || prev.subject,
        preview_text: response.data.preview_text || prev.preview_text,
        html_content: response.data.html_content || prev.html_content
      }));
      toast.success('Email content generated!');
    } catch (error) {
      console.error('Error generating email:', error);
      toast.error('Failed to generate email content');
    }
  };
  
  // Task Management
  const handleCreateTask = async () => {
    if (!selectedCampaign) {
      toast.error('Please select a campaign first');
      return;
    }
    
    try {
      await omniChannelAPI.createTask(selectedCampaign.id, taskForm);
      toast.success('Task created');
      setShowTaskDialog(false);
      setTaskForm({
        title: '',
        description: '',
        assigned_to: '',
        due_date: '',
        priority: 'medium',
        channel: ''
      });
      // Refresh campaign to show new task
      const response = await omniChannelAPI.getCampaign(selectedCampaign.id);
      setSelectedCampaign(response.data);
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    }
  };
  
  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    if (!selectedCampaign) return;
    
    try {
      await omniChannelAPI.updateTask(selectedCampaign.id, taskId, { status: newStatus });
      toast.success('Task updated');
      // Refresh campaign
      const response = await omniChannelAPI.getCampaign(selectedCampaign.id);
      setSelectedCampaign(response.data);
      loadDashboard();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };
  
  // Filter campaigns
  const filteredCampaigns = campaigns.filter(campaign => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return campaign.name?.toLowerCase().includes(search) || 
             campaign.description?.toLowerCase().includes(search);
    }
    return true;
  });
  
  // Comparison functions
  const toggleCampaignForComparison = (campaign) => {
    setSelectedForComparison(prev => {
      const exists = prev.find(c => c.id === campaign.id);
      if (exists) {
        return prev.filter(c => c.id !== campaign.id);
      }
      if (prev.length >= 5) {
        toast.error('Maximum 5 campaigns for comparison');
        return prev;
      }
      return [...prev, campaign];
    });
  };
  
  const removeCampaignFromComparison = (campaignId) => {
    setSelectedForComparison(prev => prev.filter(c => c.id !== campaignId));
    setComparisonData(null);
  };
  
  const handleCompare = async () => {
    if (selectedForComparison.length < 2) {
      toast.error('Select at least 2 campaigns to compare');
      return;
    }
    
    try {
      setComparing(true);
      const response = await omniChannelAPI.compareCampaigns(selectedForComparison.map(c => c.id));
      setComparisonData(response.data);
      toast.success('Comparison generated!');
    } catch (error) {
      console.error('Error comparing campaigns:', error);
      toast.error('Failed to compare campaigns');
    } finally {
      setComparing(false);
    }
  };
  
  const getVerdictColor = (verdict) => {
    switch (verdict) {
      case 'excellent': return 'bg-green-100 text-green-800';
      case 'good': return 'bg-blue-100 text-blue-800';
      case 'average': return 'bg-amber-100 text-amber-800';
      case 'needs_improvement': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  // Dashboard Tab
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Campaigns</p>
                <p className="text-2xl font-bold">{dashboard?.stats?.total_campaigns || 0}</p>
              </div>
              <Target className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Campaigns</p>
                <p className="text-2xl font-bold text-green-600">{dashboard?.stats?.active_campaigns || 0}</p>
              </div>
              <Zap className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Emails Sent</p>
                <p className="text-2xl font-bold">{dashboard?.stats?.total_emails_sent?.toLocaleString() || 0}</p>
              </div>
              <Mail className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Open Rate</p>
                <p className="text-2xl font-bold">{dashboard?.stats?.avg_open_rate || 0}%</p>
              </div>
              <Eye className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Tasks & Priority */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ListTodo className="h-5 w-5" />
              Task Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm">Total Tasks</span>
                <span className="font-semibold">{dashboard?.stats?.total_tasks || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-600">Completed</span>
                <span className="font-semibold text-green-600">{dashboard?.stats?.completed_tasks || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-red-600">Overdue</span>
                <span className="font-semibold text-red-600">{dashboard?.stats?.overdue_tasks || 0}</span>
              </div>
              {dashboard?.stats?.total_tasks > 0 && (
                <Progress 
                  value={(dashboard.stats.completed_tasks / dashboard.stats.total_tasks) * 100} 
                  className="h-2"
                />
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5" />
              Channel Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(dashboard?.channel_distribution || {}).map(([channel, count]) => {
                const Icon = channelIcons[channel] || Globe;
                return (
                  <div key={channel} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      <span className="text-sm capitalize">{channel.replace('_', ' ')}</span>
                    </div>
                    <Badge variant="secondary">{count}</Badge>
                  </div>
                );
              })}
              {Object.keys(dashboard?.channel_distribution || {}).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No channel data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Recent Campaigns */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Recent Campaigns
            </span>
            <Button size="sm" onClick={() => setActiveTab('campaigns')}>
              View All <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(dashboard?.recent_campaigns || []).slice(0, 5).map(campaign => (
              <div 
                key={campaign.id} 
                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted cursor-pointer"
                onClick={() => {
                  setSelectedCampaign(campaign);
                  setActiveTab('campaigns');
                }}
              >
                <div>
                  <p className="font-medium">{campaign.name}</p>
                  <div className="flex gap-2 mt-1">
                    <Badge className={statusColors[campaign.status]}>{campaign.status}</Badge>
                    <Badge className={priorityColors[campaign.priority]}>{campaign.priority}</Badge>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </div>
            ))}
            {(dashboard?.recent_campaigns || []).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No campaigns yet. Create your first one!</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
  
  // Campaigns Tab
  const renderCampaigns = () => (
    <div className="space-y-6">
      {/* Filters & Actions */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="planning">Planning</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priority</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setShowCampaignDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Campaign
        </Button>
      </div>
      
      {/* Campaign List & Detail Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Campaign List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Campaigns ({filteredCampaigns.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[600px]">
                {filteredCampaigns.map(campaign => (
                  <div
                    key={campaign.id}
                    className={`p-4 border-b cursor-pointer hover:bg-muted/50 transition-colors ${selectedCampaign?.id === campaign.id ? 'bg-muted' : ''}`}
                    onClick={async () => {
                      try {
                        const response = await omniChannelAPI.getCampaign(campaign.id);
                        setSelectedCampaign(response.data);
                      } catch (error) {
                        setSelectedCampaign(campaign);
                      }
                    }}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <p className="font-medium truncate">{campaign.name}</p>
                      <Badge className={priorityColors[campaign.priority]} variant="secondary">
                        {campaign.priority}
                      </Badge>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Badge className={statusColors[campaign.status]}>{campaign.status}</Badge>
                      {(campaign.active_channels || []).slice(0, 2).map(ch => (
                        <Badge key={ch} variant="outline" className={channelColors[ch]}>
                          {ch.replace('_', ' ')}
                        </Badge>
                      ))}
                      {(campaign.active_channels || []).length > 2 && (
                        <Badge variant="outline">+{campaign.active_channels.length - 2}</Badge>
                      )}
                    </div>
                  </div>
                ))}
                {filteredCampaigns.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">
                    <Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No campaigns found</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
        
        {/* Campaign Detail */}
        <div className="lg:col-span-2">
          {selectedCampaign ? (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{selectedCampaign.name}</CardTitle>
                    <CardDescription>{selectedCampaign.description}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleDeleteCampaign(selectedCampaign.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="overview">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="channels">Channels</TabsTrigger>
                    <TabsTrigger value="tasks">Tasks</TabsTrigger>
                    <TabsTrigger value="comments">Comments</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="overview" className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Status</Label>
                        <Badge className={statusColors[selectedCampaign.status]}>{selectedCampaign.status}</Badge>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Priority</Label>
                        <Badge className={priorityColors[selectedCampaign.priority]}>{selectedCampaign.priority}</Badge>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Start Date</Label>
                        <p>{selectedCampaign.start_date || 'Not set'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">End Date</Label>
                        <p>{selectedCampaign.end_date || 'Not set'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Budget</Label>
                        <p>${selectedCampaign.total_budget?.toLocaleString() || 0}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Spent</Label>
                        <p>${selectedCampaign.spent_budget?.toLocaleString() || 0}</p>
                      </div>
                    </div>
                    
                    {selectedCampaign.objective && (
                      <div>
                        <Label className="text-muted-foreground">Objective</Label>
                        <p className="mt-1">{selectedCampaign.objective}</p>
                      </div>
                    )}
                    
                    <Separator />
                    
                    <div>
                      <Label className="text-muted-foreground">Performance</Label>
                      <div className="grid grid-cols-3 gap-4 mt-2">
                        <div className="text-center p-3 bg-muted rounded-lg">
                          <p className="text-2xl font-bold">{selectedCampaign.total_reach?.toLocaleString() || 0}</p>
                          <p className="text-xs text-muted-foreground">Reach</p>
                        </div>
                        <div className="text-center p-3 bg-muted rounded-lg">
                          <p className="text-2xl font-bold">{selectedCampaign.total_engagement?.toLocaleString() || 0}</p>
                          <p className="text-xs text-muted-foreground">Engagements</p>
                        </div>
                        <div className="text-center p-3 bg-muted rounded-lg">
                          <p className="text-2xl font-bold">{selectedCampaign.total_conversions?.toLocaleString() || 0}</p>
                          <p className="text-xs text-muted-foreground">Conversions</p>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="channels" className="mt-4">
                    <div className="space-y-3">
                      {(selectedCampaign.channels || []).map((channel, idx) => {
                        const Icon = channelIcons[channel.channel_type] || Globe;
                        return (
                          <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${channelColors[channel.channel_type]}`}>
                                <Icon className="h-5 w-5" />
                              </div>
                              <div>
                                <p className="font-medium capitalize">{channel.channel_type.replace('_', ' ')}</p>
                                <p className="text-sm text-muted-foreground">
                                  Budget: ${channel.budget_allocation?.toLocaleString() || 0}
                                </p>
                              </div>
                            </div>
                            <Badge variant={channel.enabled ? "default" : "secondary"}>
                              {channel.enabled ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        );
                      })}
                      {(selectedCampaign.channels || []).length === 0 && (
                        <p className="text-center text-muted-foreground py-8">No channels configured</p>
                      )}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="tasks" className="mt-4">
                    <div className="flex justify-between items-center mb-4">
                      <p className="text-sm text-muted-foreground">
                        {(selectedCampaign.tasks || []).length} tasks
                      </p>
                      <Button size="sm" onClick={() => setShowTaskDialog(true)}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add Task
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {(selectedCampaign.tasks || []).map(task => {
                        const assignee = task.assigned_to ? teamMembers.find(m => m.id === task.assigned_to) : null;
                        return (
                          <div key={task.id} className="flex items-center justify-between p-3 border rounded-lg">
                            <div className="flex items-center gap-3">
                              <Checkbox 
                                checked={task.status === 'completed'}
                                onCheckedChange={(checked) => 
                                  handleUpdateTaskStatus(task.id, checked ? 'completed' : 'todo')
                                }
                              />
                              <div>
                                <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                                  {task.title}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  {task.due_date && (
                                    <span className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      {new Date(task.due_date).toLocaleDateString()}
                                    </span>
                                  )}
                                  {assignee && (
                                    <span className="flex items-center gap-1">
                                      <Users className="h-3 w-3" />
                                      {assignee.first_name} {assignee.last_name}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <Badge className={priorityColors[task.priority]}>{task.priority}</Badge>
                          </div>
                        );
                      })}
                      {(selectedCampaign.tasks || []).length === 0 && (
                        <p className="text-center text-muted-foreground py-8">No tasks yet</p>
                      )}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="comments" className="mt-4">
                    <div className="space-y-3">
                      {(selectedCampaign.comments || []).map(comment => (
                        <div key={comment.id} className="p-3 bg-muted rounded-lg">
                          <div className="flex justify-between items-start mb-2">
                            <p className="font-medium text-sm">{comment.author_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(comment.created_at).toLocaleString()}
                            </p>
                          </div>
                          <p className="text-sm">{comment.content}</p>
                        </div>
                      ))}
                      {(selectedCampaign.comments || []).length === 0 && (
                        <p className="text-center text-muted-foreground py-8">No comments yet</p>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center h-[500px] text-center">
                <Target className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Select a Campaign</h3>
                <p className="text-muted-foreground">Choose a campaign from the list to view details</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
  
  // Email Marketing Tab
  const renderEmailMarketing = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Email Marketing</h2>
          <p className="text-muted-foreground">Create and manage email campaigns</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadEmailCampaigns}>
            <RefreshCcw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowEmailDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Email Campaign
          </Button>
        </div>
      </div>
      
      {/* Email Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Mail className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{emailCampaigns.length}</p>
                <p className="text-sm text-muted-foreground">Total Campaigns</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Send className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">
                  {emailCampaigns.reduce((sum, c) => sum + (c.total_sent || 0), 0).toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">Emails Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Eye className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{dashboard?.stats?.avg_open_rate || 0}%</p>
                <p className="text-sm text-muted-foreground">Avg Open Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <MousePointerClick className="h-8 w-8 text-orange-500" />
              <div>
                <p className="text-2xl font-bold">{dashboard?.stats?.avg_click_rate || 0}%</p>
                <p className="text-sm text-muted-foreground">Avg Click Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Templates Quick Access */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Email Templates ({emailTemplates.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {emailTemplates.slice(0, 5).map(template => (
              <Card key={template.id} className="min-w-[200px] cursor-pointer hover:border-primary">
                <CardContent className="p-4">
                  <p className="font-medium truncate">{template.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{template.subject}</p>
                  <Badge variant="outline" className="mt-2">{template.category}</Badge>
                </CardContent>
              </Card>
            ))}
            {emailTemplates.length === 0 && (
              <p className="text-muted-foreground py-4">No templates yet</p>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Email Campaigns List */}
      <Card>
        <CardHeader>
          <CardTitle>Email Campaigns</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {emailCampaigns.map(campaign => (
              <div 
                key={campaign.id} 
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{campaign.name}</p>
                    <Badge variant={campaign.status === 'sent' ? 'default' : campaign.status === 'draft' ? 'secondary' : 'outline'}>
                      {campaign.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{campaign.subject}</p>
                  <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                    <span>Sent: {campaign.total_sent || 0}</span>
                    <span>Opened: {campaign.total_opened || 0}</span>
                    <span>Clicked: {campaign.total_clicked || 0}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  {campaign.status === 'draft' && (
                    <Button 
                      size="sm" 
                      onClick={() => handleSendEmailCampaign(campaign.id)}
                    >
                      <Send className="h-4 w-4 mr-1" />
                      Send
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={async () => {
                      await omniChannelAPI.deleteEmailCampaign(campaign.id);
                      toast.success('Campaign deleted');
                      loadEmailCampaigns();
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {emailCampaigns.length === 0 && (
              <div className="text-center py-12">
                <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No email campaigns yet</p>
                <Button className="mt-4" onClick={() => setShowEmailDialog(true)}>
                  Create Your First Campaign
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Contact Lists */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Contact Lists ({contactLists.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {contactLists.map(list => (
              <Card key={list.id}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{list.name}</p>
                      <p className="text-sm text-muted-foreground">{list.description}</p>
                    </div>
                    <Badge variant={list.is_dynamic ? 'default' : 'secondary'}>
                      {list.is_dynamic ? 'Dynamic' : 'Static'}
                    </Badge>
                  </div>
                  <p className="text-2xl font-bold mt-2">{list.contact_count}</p>
                  <p className="text-xs text-muted-foreground">contacts</p>
                </CardContent>
              </Card>
            ))}
            {contactLists.length === 0 && (
              <p className="col-span-3 text-center text-muted-foreground py-8">No contact lists yet</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
  
  // Calendar Tab
  const renderCalendar = () => {
    const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
    
    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    const getEventsForDay = (day) => {
      if (!day) return [];
      const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      return calendarEvents.filter(event => event.start?.startsWith(dateStr) || event.start_date?.startsWith(dateStr));
    };
    
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold">Campaign Calendar</h2>
            <p className="text-muted-foreground">View all campaigns and events</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
            >
              Previous
            </Button>
            <span className="font-medium px-4">
              {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
            </span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
            >
              Next
            </Button>
          </div>
        </div>
        
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-7 gap-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center font-medium text-sm py-2 text-muted-foreground">
                  {day}
                </div>
              ))}
              {days.map((day, idx) => {
                const events = getEventsForDay(day);
                return (
                  <div 
                    key={idx} 
                    className={`min-h-[100px] p-2 border rounded-lg ${day ? 'bg-background' : 'bg-muted/30'}`}
                  >
                    {day && (
                      <>
                        <p className="text-sm font-medium mb-1">{day}</p>
                        <div className="space-y-1">
                          {events.slice(0, 3).map((event, eventIdx) => (
                            <div 
                              key={eventIdx}
                              className="text-xs p-1 rounded truncate"
                              style={{ backgroundColor: event.color || '#3b82f6', color: 'white' }}
                            >
                              {event.title}
                            </div>
                          ))}
                          {events.length > 3 && (
                            <p className="text-xs text-muted-foreground">+{events.length - 3} more</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };
  
  // Comparison Tab
  const renderComparison = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Scale className="h-5 w-5" />
            Campaign Performance Comparison
          </h2>
          <p className="text-muted-foreground">Compare up to 5 campaigns side-by-side</p>
        </div>
        <Button onClick={() => setShowCompareDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Select Campaigns
        </Button>
      </div>
      
      {/* Selected Campaigns */}
      {selectedForComparison.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Selected Campaigns ({selectedForComparison.length}/5)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 mb-4">
              {selectedForComparison.map((campaign, idx) => (
                <Badge
                  key={campaign.id}
                  variant="outline"
                  className="px-3 py-1.5 text-sm flex items-center gap-2"
                  style={{ borderLeftColor: CHART_COLORS[idx], borderLeftWidth: 4 }}
                >
                  {campaign.name}
                  <button onClick={() => removeCampaignFromComparison(campaign.id)} className="hover:text-red-500">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={handleCompare} disabled={comparing || selectedForComparison.length < 2}>
                {comparing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Scale className="h-4 w-4 mr-2" />
                )}
                Compare Now
              </Button>
              <Button variant="outline" onClick={() => { setSelectedForComparison([]); setComparisonData(null); }}>
                Clear All
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Comparison Results */}
      {comparisonData && (
        <div className="space-y-6">
          {/* Winner Banner */}
          {comparisonData.best_performer_id && (
            <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Trophy className="h-8 w-8 text-amber-500" />
                  <div>
                    <p className="font-semibold text-lg">Best Performer</p>
                    <p className="text-muted-foreground">
                      {comparisonData.comparison.find(c => c.campaign_id === comparisonData.best_performer_id)?.campaign_name}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* AI Insights */}
          {comparisonData.ai_insights && !comparisonData.ai_insights.error && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-purple-500" />
                  AI Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {comparisonData.ai_insights.summary && (
                    <p className="text-muted-foreground">{comparisonData.ai_insights.summary}</p>
                  )}
                  {comparisonData.ai_insights.winner_analysis && (
                    <div>
                      <p className="font-medium text-sm mb-1">Why the winner performed well:</p>
                      <p className="text-sm text-muted-foreground">{comparisonData.ai_insights.winner_analysis}</p>
                    </div>
                  )}
                  {comparisonData.ai_insights.key_insight && (
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <p className="text-sm font-medium text-purple-800">Key Takeaway: {comparisonData.ai_insights.key_insight}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Score Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {comparisonData.comparison.map((campaign, idx) => (
              <Card 
                key={campaign.campaign_id} 
                className={campaign.campaign_id === comparisonData.best_performer_id ? 'ring-2 ring-amber-400' : ''}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: CHART_COLORS[idx] }}
                      />
                      {campaign.campaign_name}
                    </CardTitle>
                    {campaign.campaign_id === comparisonData.best_performer_id && (
                      <Trophy className="h-5 w-5 text-amber-500" />
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-center mb-4">
                    <p className="text-4xl font-bold">{campaign.overall_score}</p>
                    <Badge className={getVerdictColor(campaign.verdict)}>{campaign.verdict_label}</Badge>
                  </div>
                  <Separator className="my-3" />
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-muted-foreground">Reach</p>
                      <p className="font-medium">{campaign.metrics.reach.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Engagement</p>
                      <p className="font-medium">{campaign.metrics.engagement.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Conversions</p>
                      <p className="font-medium">{campaign.metrics.conversions.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">ROI</p>
                      <p className={`font-medium ${campaign.metrics.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {campaign.metrics.roi}%
                      </p>
                    </div>
                  </div>
                  <Separator className="my-3" />
                  <div className="text-sm">
                    <p className="text-muted-foreground">Budget</p>
                    <div className="flex justify-between">
                      <span>${campaign.metrics.spent.toLocaleString()} / ${campaign.metrics.budget.toLocaleString()}</span>
                      <span>{campaign.metrics.budget_utilization}%</span>
                    </div>
                    <Progress value={campaign.metrics.budget_utilization} className="h-1.5 mt-1" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Performance Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Performance Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={comparisonData.chart_data.performance}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Reach" fill="#3b82f6" />
                    <Bar dataKey="Engagement" fill="#10b981" />
                    <Bar dataKey="Conversions" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            
            {/* Rates Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Rates & ROI</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={comparisonData.chart_data.rates}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Engagement Rate" fill="#8b5cf6" />
                    <Bar dataKey="Conversion Rate" fill="#ef4444" />
                    <Bar dataKey="ROI" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            
            {/* Budget Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Budget Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={comparisonData.chart_data.budget}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Budget" fill="#94a3b8" />
                    <Bar dataKey="Spent" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            
            {/* Score Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Overall Scores</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={comparisonData.chart_data.scores} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="Score" fill="#3b82f6">
                      {comparisonData.chart_data.scores.map((entry, index) => (
                        <rect key={index} fill={CHART_COLORS[index]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
      
      {/* Empty State */}
      {selectedForComparison.length === 0 && !comparisonData && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-[400px] text-center">
            <Scale className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Compare Campaign Performance</h3>
            <p className="text-muted-foreground mb-4">Select 2-5 campaigns to see side-by-side metrics, charts, and AI insights</p>
            <Button onClick={() => setShowCompareDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Select Campaigns to Compare
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

  return (
    <div className="p-6" data-testid="omni-channel-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Omni-Channel Campaigns</h1>
        <p className="text-muted-foreground">Unified campaign management across all marketing channels</p>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-2xl grid-cols-5 mb-6">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="h-4 w-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="tab-campaigns">
            <Target className="h-4 w-4 mr-2" />
            Campaigns
          </TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">
            <Mail className="h-4 w-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger value="compare" data-testid="tab-compare">
            <Scale className="h-4 w-4 mr-2" />
            Compare
          </TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">
            <Calendar className="h-4 w-4 mr-2" />
            Calendar
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="dashboard">{renderDashboard()}</TabsContent>
        <TabsContent value="campaigns">{renderCampaigns()}</TabsContent>
        <TabsContent value="email">{renderEmailMarketing()}</TabsContent>
        <TabsContent value="compare">{renderComparison()}</TabsContent>
        <TabsContent value="calendar">{renderCalendar()}</TabsContent>
      </Tabs>
      
      {/* Create Campaign Dialog */}
      <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Omni-Channel Campaign</DialogTitle>
            <DialogDescription>Set up a new multi-channel marketing campaign</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="campaign-name">Campaign Name *</Label>
                <Input
                  id="campaign-name"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Summer Sale 2026"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="campaign-desc">Description</Label>
                <Textarea
                  id="campaign-desc"
                  value={campaignForm.description}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Campaign description..."
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="campaign-objective">Objective</Label>
                <Input
                  id="campaign-objective"
                  value={campaignForm.objective}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, objective: e.target.value }))}
                  placeholder="Increase sales by 20%"
                />
              </div>
              <div>
                <Label>Priority</Label>
                <Select 
                  value={campaignForm.priority} 
                  onValueChange={(v) => setCampaignForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={campaignForm.start_date}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="end-date">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={campaignForm.end_date}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="budget">Total Budget ($)</Label>
                <Input
                  id="budget"
                  type="number"
                  value={campaignForm.total_budget}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, total_budget: parseFloat(e.target.value) || 0 }))}
                  placeholder="10000"
                />
              </div>
              <div className="col-span-2">
                <Label>Channels</Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {['email', 'social_media', 'sms', 'push_notification', 'display_ads', 'search_ads'].map(channel => {
                    const Icon = channelIcons[channel] || Globe;
                    const isSelected = campaignForm.channels.some(c => c.channel_type === channel);
                    return (
                      <Button
                        key={channel}
                        type="button"
                        variant={isSelected ? 'default' : 'outline'}
                        size="sm"
                        className="justify-start"
                        onClick={() => {
                          setCampaignForm(prev => ({
                            ...prev,
                            channels: isSelected
                              ? prev.channels.filter(c => c.channel_type !== channel)
                              : [...prev.channels, { channel_type: channel, enabled: true, priority: 1, budget_allocation: 0 }]
                          }));
                        }}
                      >
                        <Icon className="h-4 w-4 mr-1" />
                        <span className="capitalize text-xs">{channel.replace('_', ' ')}</span>
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCampaignDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCampaign} disabled={!campaignForm.name}>
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Email Campaign Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Email Campaign</DialogTitle>
            <DialogDescription>Design and send email campaigns</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label htmlFor="email-name">Campaign Name *</Label>
              <Input
                id="email-name"
                value={emailForm.name}
                onChange={(e) => setEmailForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Monthly Newsletter"
              />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <Label htmlFor="email-subject">Subject Line *</Label>
                <Input
                  id="email-subject"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Don't miss our exclusive offer!"
                />
              </div>
              <Button 
                variant="outline" 
                className="mt-6"
                onClick={handleGenerateEmailContent}
              >
                <Sparkles className="h-4 w-4 mr-1" />
                AI Generate
              </Button>
            </div>
            <div>
              <Label htmlFor="email-preview">Preview Text</Label>
              <Input
                id="email-preview"
                value={emailForm.preview_text}
                onChange={(e) => setEmailForm(prev => ({ ...prev, preview_text: e.target.value }))}
                placeholder="This is what appears in email preview..."
              />
            </div>
            <div>
              <Label htmlFor="email-content">Email Content (HTML)</Label>
              <Textarea
                id="email-content"
                value={emailForm.html_content}
                onChange={(e) => setEmailForm(prev => ({ ...prev, html_content: e.target.value }))}
                placeholder="<h1>Hello {{first_name}}!</h1><p>Your content here...</p>"
                rows={6}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Use {'{{first_name}}'}, {'{{last_name}}'}, {'{{email}}'} for personalization
              </p>
            </div>
            <div>
              <Label>Recipient List</Label>
              <Select 
                value={emailForm.recipient_list_id} 
                onValueChange={(v) => setEmailForm(prev => ({ ...prev, recipient_list_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a contact list" />
                </SelectTrigger>
                <SelectContent>
                  {contactLists.map(list => (
                    <SelectItem key={list.id} value={list.id}>
                      {list.name} ({list.contact_count} contacts)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="schedule">Schedule (Optional)</Label>
              <Input
                id="schedule"
                type="datetime-local"
                value={emailForm.scheduled_at}
                onChange={(e) => setEmailForm(prev => ({ ...prev, scheduled_at: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateEmailCampaign} disabled={!emailForm.name || !emailForm.subject}>
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
            <DialogDescription>Create a new task for this campaign</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label htmlFor="task-title">Title *</Label>
              <Input
                id="task-title"
                value={taskForm.title}
                onChange={(e) => setTaskForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Review email copy"
              />
            </div>
            <div>
              <Label htmlFor="task-desc">Description</Label>
              <Textarea
                id="task-desc"
                value={taskForm.description}
                onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Task details..."
              />
            </div>
            <div>
              <Label>Assign To</Label>
              <AssigneeSelect
                value={taskForm.assigned_to}
                onValueChange={(v) => setTaskForm(prev => ({ ...prev, assigned_to: v === 'unassigned' ? '' : v }))}
                includeAI={true}
                placeholder="Select assignee"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Priority</Label>
                <Select 
                  value={taskForm.priority} 
                  onValueChange={(v) => setTaskForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="task-due">Due Date</Label>
                <Input
                  id="task-due"
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTask} disabled={!taskForm.title}>Add Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Campaign Selection Dialog for Comparison */}
      <Dialog open={showCompareDialog} onOpenChange={setShowCompareDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Select Campaigns to Compare</DialogTitle>
            <DialogDescription>
              Choose 2-5 campaigns for side-by-side comparison. Selected: {selectedForComparison.length}/5
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-2">
              {campaigns.map(campaign => {
                const isSelected = selectedForComparison.some(c => c.id === campaign.id);
                return (
                  <div 
                    key={campaign.id}
                    onClick={() => toggleCampaignForComparison(campaign)}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-50 border-blue-300' : 'hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Checkbox checked={isSelected} readOnly />
                        <div>
                          <p className="font-medium">{campaign.name}</p>
                          <div className="flex gap-2 mt-1">
                            <Badge className={statusColors[campaign.status]} variant="secondary">
                              {campaign.status}
                            </Badge>
                            <Badge className={priorityColors[campaign.priority]} variant="secondary">
                              {campaign.priority}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground text-right">
                        <p>{campaign.active_channels?.length || 0} channels</p>
                        <p>Budget: ${campaign.total_budget?.toLocaleString() || 0}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
              {campaigns.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Target className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No campaigns available. Create some campaigns first!</p>
                </div>
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompareDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => { setShowCompareDialog(false); handleCompare(); }} 
              disabled={selectedForComparison.length < 2}
            >
              <Scale className="h-4 w-4 mr-2" />
              Compare ({selectedForComparison.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
